import React from "react";

/**
 * Tiny inline SVG sparkline. Renders numeric trend as a smooth polyline with an
 * area fill, a subtle baseline, and a highlighted last point.
 *
 * props:
 *  - values: number[]  (chronological, oldest → newest)
 *  - width, height: dimensions in px
 *  - color: stroke color
 *  - fill: area fill color (rgba/hex)
 *  - label: caption above the sparkline
 *  - suffix: unit shown next to the last value (e.g. "¥", "d", " units")
 *  - deltaMode: how to interpret good/bad direction — "lower" (cost) means down=green, up=red; "higher" means opposite.
 */
export default function Sparkline({
  values = [],
  width = 140,
  height = 36,
  color = "#0ea5e9",
  fill = "rgba(14,165,233,0.15)",
  label,
  suffix = "",
  deltaMode = "lower",
}) {
  const nums = (values || []).filter((v) => v !== null && v !== undefined && !Number.isNaN(Number(v))).map(Number);
  if (nums.length < 2) {
    return (
      <div className="text-[10px] text-slate-400 font-mono">
        {label && <div className="uppercase tracking-wider text-slate-500 font-semibold">{label}</div>}
        <span>Not enough data</span>
      </div>
    );
  }
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const range = max - min || 1;
  const step = nums.length > 1 ? width / (nums.length - 1) : width;
  const points = nums.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * height;
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${path} L${width.toFixed(1)},${height} L0,${height} Z`;

  const first = nums[0];
  const last = nums[nums.length - 1];
  const delta = last - first;
  const pct = first !== 0 ? (delta / Math.abs(first)) * 100 : 0;
  const [lx, ly] = points[points.length - 1];

  const isGood = deltaMode === "lower" ? delta < 0 : delta > 0;
  const isNeutral = delta === 0;
  const deltaClass = isNeutral ? "text-slate-500" : isGood ? "text-emerald-700" : "text-red-700";
  const arrow = isNeutral ? "→" : delta > 0 ? "↑" : "↓";

  return (
    <div>
      {label && (
        <div className="flex items-baseline justify-between mb-0.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
          <span className={`text-[10px] font-mono font-semibold ${deltaClass}`}>
            {arrow} {Math.abs(pct).toFixed(1)}%
          </span>
        </div>
      )}
      <div className="flex items-center gap-2">
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
          <path d={area} fill={fill} stroke="none" />
          <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={lx} cy={ly} r="2.5" fill={color} />
        </svg>
        <div className="font-mono text-[12.5px] font-bold text-slate-900 leading-none">
          {typeof last === "number" ? last.toLocaleString(undefined, { maximumFractionDigits: 2 }) : last}
          {suffix && <span className="text-[10px] font-normal text-slate-500 ml-0.5">{suffix}</span>}
        </div>
      </div>
    </div>
  );
}
